import type { SubmitScanRequest } from '@/api/types'
import type { TranslationKey } from '@/i18n/translations'
import type { ScanEventDescription } from './fh-vitals-events'
import { describeMindsetSignals } from './mindset-events'
import { toSubmitRequestFromMindset } from './mindset-result'
import { ELAPSED, raceElapsed } from './session-timeouts'
import type {
  MindsetSessionConfig,
  MindsetStopPayload,
  MindsetVital,
  MindsetVitalClient,
} from './mindset-types'

/** What the vendor needs to know about the patient. Height and weight are not among them. */
export interface MindsetDemographics {
  readonly age: number
  /** The vendor's own vocabulary: it takes M or F and has no third value (see toMindsetGender). */
  readonly gender: 'M' | 'F'
}

export interface MindsetSessionConfiguration {
  /** How long to wait for the SDK's `ready` event before giving up on the WASM module loading. */
  readonly readyTimeoutMs: number
  /** How long to wait after `authorize()` for the `authorizedVitals` event, if it has not arrived. */
  readonly authorizedVitalsTimeoutMs: number
  /**
   * The hard cap on one measurement. The SDK normally stops itself once every vital has settled;
   * this is what bounds the scan when it does not, and it is the reason `stop()` is called by this
   * runner rather than only listened for.
   */
  readonly measureWindowMs: number
  /** The element the camera is attached to and the SDK reads frames from. */
  readonly video: HTMLVideoElement
}

export type MindsetSessionOutcome =
  | { readonly outcome: 'measured'; readonly request: SubmitScanRequest }
  | { readonly outcome: 'no-usable-result' }
  | { readonly outcome: 'error'; readonly messageKey: TranslationKey }

/**
 * Where a scan has got to, in the patient's terms rather than the vendor's.
 *
 * It exists because a phone told us nothing. On iOS the scan reached the measuring window on a laptop
 * and stopped somewhere before it on a phone, and everything the patient could see - a preview and a
 * bar that only moves once the VENDOR starts ticking - looked identical for "still loading 55 MB" and
 * "stuck". A phone has no console to open, so the phase is on the screen (docs/decisions.md D-26).
 */
export type MindsetPhase = 'loading' | 'licensing' | 'measuring'

/**
 * Everything this app has heard from a Mindset client, subscribed once and read by whoever is
 * driving it.
 *
 * **It is a value and not a closure over one runner, because there are two runners now.** The
 * single-vendor path below and the hybrid path (hybrid-session.ts) subscribe to the same seven
 * events for the same reasons, and the subscriptions have to be taken BEFORE `init()` - `ready` is
 * emitted during it, so a listener attached afterwards is a listener that never fires. Duplicating
 * that wiring in the hybrid runner would be duplicating the one part of this integration where a
 * missed event is an invisible hang.
 */
export interface MindsetObservation {
  /**
   * The vitals the vendor's licence covers, as they arrive.
   *
   * A LIVE array, deliberately: `authorizedVitals` can be emitted more than once and the caller
   * reads this after awaiting `gotAuthorizedVitals`, which is the moment the first one landed. A
   * snapshot taken at subscription time would always be empty.
   */
  readonly authorized: readonly MindsetVital[]
  /** Resolves when the SDK says its WASM module is up. */
  readonly ready: Promise<void>
  /** Resolves when the licence handshake has named at least one vital. */
  readonly gotAuthorizedVitals: Promise<void>
  /** Resolves when the vendor ends the session itself. */
  readonly gotStop: Promise<void>
  /**
   * Resolves when the vendor says its HEART RATE HAS SETTLED - its own `S-HRT-061`.
   *
   * **The vendor's signal, not a stability test this app invented.** `vital-client.js` (readable in
   * the sourcemap beside the bundle) sets `hrCanEndEarly = true` on exactly that code, and its
   * `canEndEarly()` is what ends a session before `maxSessionTime` when every selected vital has
   * signalled. So this is the moment the SDK itself considers the pulse done - which is what a
   * sequential scan releases the patient on (docs/decisions.md D-42), and it is why nothing here
   * computes a variance over a window of samples this app would have to justify.
   */
  readonly hrSettled: Promise<void>
  /** The vendor's own stop payload, once it has emitted one. Null until then. */
  stopped(): MindsetStopPayload | null
}

/**
 * Subscribes to everything a Mindset run reports and hands back the promises and values a runner
 * reads.
 *
 * MUST be called before `init()`. See `MindsetObservation`.
 */
export function observeMindsetClient(
  client: MindsetVitalClient,
  /**
   * Every signal the vendor is currently raising, together.
   *
   * A LIST and not one description: the vendor's `dataStatus` routinely holds several at once - stay
   * still AND improve your lighting - and the screen shows them together (docs/decisions.md D-28).
   */
  onSignals?: (descriptions: ScanEventDescription[]) => void,
  onProgress?: (fractionComplete: number) => void,
): MindsetObservation {
  const authorized: MindsetVital[] = []
  let stopped: MindsetStopPayload | null = null
  let resolveReady!: () => void
  let resolveAuthorized!: () => void
  let resolveStopped!: () => void

  const ready = new Promise<void>((resolve) => {
    resolveReady = resolve
  })
  const gotAuthorizedVitals = new Promise<void>((resolve) => {
    resolveAuthorized = resolve
  })
  const gotStop = new Promise<void>((resolve) => {
    resolveStopped = resolve
  })
  let resolveHrSettled!: () => void
  const hrSettled = new Promise<void>((resolve) => {
    resolveHrSettled = resolve
  })

  client.on('ready', () => resolveReady())

  client.on('authorizedVitals', (vitals) => {
    for (const vital of vitals) {
      if (!authorized.includes(vital)) {
        authorized.push(vital)
      }
    }
    resolveAuthorized()
  })

  let lastCodes: string | null = null

  client.on('signsMessage', (message) => {
    // The vendor's running commentary, raw, for the same reason the final result is logged below: the
    // per-second quality codes are how a scan that ends with nothing is explained, and they exist
    // nowhere but this console.
    //
    // ON EVERY CHANGE, AND NOT ON EVERY FRAME, which is what this used to be - and on a phone that
    // was a memory leak rather than a log (docs/decisions.md D-38). "Per-second" was wrong about the
    // vendor: `signsMessage` arrives with every frame its pump reads, 30 to 60 times a second, and
    // each line hands the console a live reference to a vendor object holding a vitals snapshot. With
    // Web Inspector attached nothing on that list can ever be collected, so a two-minute scan retains
    // thousands of them - on the one device in this integration that has a hard per-tab memory
    // ceiling, running two WASM runtimes at once.
    //
    // The diagnostic value is entirely in the CODES and in when they changed, which is what a scan
    // that ended with nothing is explained by. Sixty identical `S-GEN-002` lines a second explain
    // nothing and bury the line that does. This is the rule shenai-session.ts's state log already
    // applies to a vendor read on a timer (D-35), arriving late at the vendor that pushes.
    const codes = (message.dataStatus ?? []).map((status) => status.code ?? '').join(',')

    // The vendor's own "the pulse is settled" (see MindsetObservation.hrSettled). Read off the same
    // per-frame feed and not from a separate subscription, because it IS one of these codes - the SDK
    // publishes its readiness in the same array it publishes its complaints in.
    if (codes.includes(HEART_RATE_SETTLED_CODE)) {
      resolveHrSettled()
    }

    if (codes !== lastCodes) {
      console.log('[mindset] signs', codes, message)
      lastCodes = codes
    }

    // EVERY signal the vendor is raising, so a patient fixing one is not left wondering about the
    // other. The screen shows them together in red (docs/decisions.md D-28); the runner passes them on
    // and decides nothing about them. Still on every frame: this is screen state, it costs a
    // comparison and a `setState` React discards when nothing changed, and a signal the patient is
    // shown late is a signal that arrived late.
    onSignals?.(describeMindsetSignals(message))
  })

  client.on('stop', (payload) => {
    stopped ??= payload
    resolveStopped()
  })

  /**
   * The vendor's own account of how far through the measurement it is, which is what the patient's
   * progress bar is driven by (scanning-screen.tsx).
   *
   * **`percentComplete`, and not the `timeLeft` the event is named after.** The event carries both -
   * the vendor's bundle emits `{timeLeft, maxTime, percentComplete, percentRemaining}` on a
   * one-second interval, starting at `percentComplete: 0` - and the countdown is deliberately dropped
   * here rather than in the component, so that there is nowhere downstream for a seconds display to
   * be reintroduced from. A remaining-seconds number is not something this app can honestly show: the
   * SDK ends a session the moment its models converge, and the window above is what bounds one that
   * does not, so the vendor's clock is a description of the vendor's own upper bound and not of when
   * this patient can put their phone down.
   *
   * The value is a FRACTION of one, not a percentage - the bundle computes it as `1 - timeLeft/maxTime`
   * - and the prototype this was ported from multiplied by 100 at the point of display for the same
   * reason. Guarded rather than trusted, because it arrives from a minified vendor bundle across an
   * `unknown`-typed event boundary and a NaN width is a bar that vanishes mid-scan; the clamp to 0..1
   * lives in the component, which is where the number becomes a length.
   */
  client.on('timeLeft', (payload) => {
    const fraction = payload?.percentComplete
    if (typeof fraction === 'number' && Number.isFinite(fraction)) {
      onProgress?.(fraction)
    }
  })

  // The vendor's `error` event is deliberately NOT turned into a patient-facing sentence: it carries
  // the vendor's own words (a licence message, an internal state name), and architecture §9 is that
  // a patient reads a sentence this app wrote. A genuine failure surfaces as a rejected promise
  // below or as a session that produces nothing, both of which are already handled.
  client.on('error', () => {})

  return {
    authorized,
    ready,
    gotAuthorizedVitals,
    gotStop,
    hrSettled,
    stopped: () => stopped,
  }
}

/**
 * The vendor's code for "a heart rate result is in and the session could end on it".
 *
 * One of the four "ready" codes `mindset-events.ts` deliberately shows the patient NOTHING for - they
 * report progress rather than trouble, and a scan going well should not flash a banner. Reading one of
 * them for a different purpose does not change that: the patient still sees nothing, the screen just
 * stops asking them to hold still (docs/decisions.md D-42).
 */
export const HEART_RATE_SETTLED_CODE = 'S-HRT-061'

/** Whether a Mindset client got as far as being able to measure, and what it may measure. */
export type MindsetPreparation =
  | { readonly prepared: true; readonly vitals: readonly MindsetVital[] }
  | { readonly prepared: false; readonly messageKey: TranslationKey }

/**
 * Everything before the measurement: init → ready → preview → authorize → the licensed vitals.
 *
 * **Its own function because the hybrid path has to stop here.** A hybrid scan loads both vendors
 * and starts neither until both are ready (docs/decisions.md D-37), which is only expressible if
 * "get ready" and "measure" are two calls. The single-vendor runner below makes them back to back,
 * which is what it always did.
 *
 * @param onPhase Announced to the patient's screen AND to the console, because a phone has only one
 * of the two and "still loading 55 MB" looked exactly like "stuck" on it (docs/decisions.md D-26).
 */
export async function prepareMindsetSession(
  client: MindsetVitalClient,
  observation: MindsetObservation,
  config: Pick<MindsetSessionConfiguration, 'readyTimeoutMs' | 'authorizedVitalsTimeoutMs' | 'video'>,
  onPhase?: (phase: MindsetPhase) => void,
): Promise<MindsetPreparation> {
  const phase = (next: MindsetPhase) => {
    console.log('[mindset] phase', next)
    onPhase?.(next)
  }

  phase('loading')

  // TIMED, and it was not. `readyTimeoutMs` guarded only the vendor's `ready` EVENT, which cannot
  // arrive before `init()` resolves - so an init that never resolved was a patient left on "Getting
  // the scanner ready" for as long as they were willing to hold the phone. That is what an iPhone did
  // (docs/decisions.md D-27): 29 MB of WASM and data, on a device with a hard per-tab memory ceiling,
  // and nothing in this app would ever have given up on it.
  //
  // The whole prologue shares one budget rather than each half having its own. What the patient is
  // waiting for is "can this scan start at all", and two timeouts that each look reasonable add up to
  // twice as long staring at a screen that says the same thing throughout.
  const startedLoading = Date.now()
  if ((await raceElapsed(config.readyTimeoutMs, client.init())) === ELAPSED) {
    console.warn('[mindset] the vendor never finished loading', { afterMs: Date.now() - startedLoading })
    return { prepared: false, messageKey: 'scannerCouldNotLoad' }
  }

  const remaining = Math.max(1_000, config.readyTimeoutMs - (Date.now() - startedLoading))
  if ((await raceElapsed(remaining, observation.ready)) === ELAPSED) {
    console.warn('[mindset] the vendor loaded but never said it was ready', { afterMs: Date.now() - startedLoading })
    return { prepared: false, messageKey: 'scannerCouldNotLoad' }
  }

  await client.startPreviewMode(config.video)

  phase('licensing')
  await client.authorize()

  if (observation.authorized.length === 0) {
    await raceElapsed(config.authorizedVitalsTimeoutMs, observation.gotAuthorizedVitals)
  }

  if (observation.authorized.length === 0) {
    // The licence handshake resolved and the vendor named nothing it is entitled to measure. That
    // is a clinic-entitlement problem, not something the patient can fix by trying again - but the
    // patient is told what every other unstartable scan says, because the difference belongs in
    // the console and in this repository's own logs.
    return { prepared: false, messageKey: 'scanCouldNotStart' }
  }

  return { prepared: true, vitals: observation.authorized }
}

/**
 * The measurement itself: start, wait for the vendor or for the window, stop.
 *
 * **Why this asks the vendor to `stop()` even though the SDK emits `stop`.** The vendor ends a
 * session on its own once its models have converged, and the reference integration in
 * `SH/multi-sdk` still carries a 110-second watchdog because it observed sessions that never did. A
 * patient holding a phone to their face is not a good place to find out which kind of session this
 * was, so the window is this app's and the SDK's own `stop` is taken as an early finish rather than
 * as the only finish.
 *
 * @returns The vendor's stop payload, or null if it produced none at all.
 */
export async function measureMindsetSession(
  client: MindsetVitalClient,
  observation: MindsetObservation,
  vitals: readonly MindsetVital[],
  demographics: MindsetDemographics,
  config: Pick<MindsetSessionConfiguration, 'measureWindowMs' | 'video'>,
): Promise<MindsetStopPayload | null> {
  const session: MindsetSessionConfig = {
    vitals,
    data: { user: { age: demographics.age, gender: demographics.gender } },
  }

  await client.start(session, config.video)

  // Whichever comes first: the SDK finishing on its own, or the window closing.
  await raceElapsed(config.measureWindowMs, observation.gotStop)

  return observation.stopped() ?? (await client.stop())
}

/**
 * Runs one Mindset scan end to end: init → ready → preview → authorize → start → the measurement
 * window → stop → the platform's submit shape.
 *
 * Takes a `MindsetVitalClient` rather than reaching for the vendor's factory itself, which is what
 * lets every branch below be driven by a fake under Vitest's fake timers instead of by a camera, an
 * 11 MB WASM module and a real minute.
 *
 * `onProgress` is the second reporting callback, beside `onSignals`, and is a callback for the same
 * reason: what the screen does with either is the screen's business, and this runner is the only thing
 * in the app that has heard from the vendor.
 */
export async function runMindsetScanSession(
  client: MindsetVitalClient,
  demographics: MindsetDemographics,
  config: MindsetSessionConfiguration,
  onSignals?: (descriptions: ScanEventDescription[]) => void,
  onProgress?: (fractionComplete: number) => void,
  onPhase?: (phase: MindsetPhase) => void,
  /**
   * The vendor's own VITAL-TRAC structure, handed over so the flow can copy it to the vendor's
   * records when this installation forwards results (docs/decisions.md D-33).
   *
   * A CALLBACK rather than a field on the outcome, for the same reason `onSignals` and `onProgress`
   * are callbacks: the outcome is what this app DID with the scan - measured, nothing usable, failed -
   * and it is consumed by a flow that maps it onto a screen. The vendor's raw payload is not that; it
   * is a side channel to a third party, and putting it in the union would put it in front of every
   * reader of every branch.
   *
   * Called for a session that produced a result, whether or not this app could map it: a reading this
   * app rejects as unusable is still the vendor's own measurement, and the vendor's records are where
   * it can be looked at.
   */
  onVendorResult?: (result: unknown) => void,
): Promise<MindsetSessionOutcome> {
  const observation = observeMindsetClient(client, onSignals, onProgress)

  try {
    const preparation = await prepareMindsetSession(client, observation, config, onPhase)

    if (!preparation.prepared) {
      return { outcome: 'error', messageKey: preparation.messageKey }
    }

    console.log('[mindset] phase', 'measuring')
    onPhase?.('measuring')

    const stopped = await measureMindsetSession(
      client,
      observation,
      preparation.vitals,
      demographics,
      config,
    )

    // The vendor's whole VITAL-TRAC structure - `stopped.result`, not the event envelope around it
    // (which also carries sessionComplete and remainingSessionAttempts). The vendor's own
    // documentation calls what belongs in pro_data.data "the complete vitals result object from the
    // SDK's stop event", and this is that object.
    if (stopped?.result) {
      onVendorResult?.(stopped.result)
    }

    const request = toSubmitRequestFromMindset(stopped?.result?.finalVitalsMeasurementValues)

    // THE VENDOR'S RAW ANSWER, ON THE CONSOLE, DELIBERATELY.
    //
    // Not a debug leftover: the vendor's measurement is the one thing in this flow that this
    // repository cannot see from anywhere else. The submit body carries four integers (D-15), the
    // service stores what it is sent, and everything the SDK reported ABOUT those numbers - which
    // vitals it was licensed for, its own confidence and data-quality codes, the values it discarded -
    // exists only in the browser for the length of one scan. When a scan produces no usable reading,
    // this log is the difference between "the vendor measured nothing" and "the vendor measured
    // something this app failed to map" (docs/decisions.md D-24).
    //
    // It is not gated behind a development define. The deployed dev installation runs a production
    // build - `__FH_CAMERA_REPLACEMENT__` is false there - so a gated log would be absent from the
    // only environment anyone debugs a real vendor run in. It stays on the PATIENT'S OWN device, is
    // their own measurement, and is never sent anywhere by this line; the vendor's own bundle already
    // logs far more to the same console.
    console.log('[mindset] raw vendor result', stopped?.result)
    console.log('[mindset] vitals the licence covered', observation.authorized)
    console.log('[mindset] mapped submit request', request ?? null)

    return request ? { outcome: 'measured', request } : { outcome: 'no-usable-result' }
  } catch {
    // Everything the vendor can throw at us - a rejected init, a camera the browser refused, an
    // authenticator that could not reach this service - is one sentence to the patient. Which of
    // them it was is in the console, and the alternative is showing a medical page's visitor a
    // vendor exception.
    return { outcome: 'error', messageKey: 'scanCouldNotStart' }
  } finally {
    try {
      client.destroy()
    } catch {
      // A vendor that throws while being torn down has already given us whatever it was going to.
    }
  }
}
